import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { useParams, useNavigate } from "react-router-dom";
import { useAdmin } from "../../../context/AdminContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const MAX_NAME = 80;
const MAX_DETAILS = 1000;
const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const MAX_OTHER_IMAGES = 10;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const CATEGORIES = [
  "Hotels & Restaurants",
  "Tea Factories & Industries",
  "Home & Living",
  "Others",
];

const normalizeImages = (images) => {
  if (!Array.isArray(images)) return [];

  return images
    .map((img) => {
      if (typeof img === "string") {
        return { url: img, public_id: "" };
      }

      if (img && typeof img.url === "string") {
        return {
          url: img.url,
          public_id: typeof img.public_id === "string" ? img.public_id : "",
        };
      }

      return null;
    })
    .filter(Boolean);
};

const isBlobUrl = (value) =>
  typeof value === "string" && value.startsWith("blob:");

const EditProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { admin, token } = useAdmin();
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [name, setName] = useState("");
  const [details, setDetails] = useState("");
  const [category, setCategory] = useState("");
  const [starred, setStarred] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [existingCover, setExistingCover] = useState("");
  const [coverPreview, setCoverPreview] = useState("");
  const [existingImages, setExistingImages] = useState([]);
  const [originalImages, setOriginalImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ global: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const revokePreview = (preview) => {
    if (isBlobUrl(preview)) {
      URL.revokeObjectURL(preview);
    }
  };

  useEffect(() => {
    return () => {
      revokePreview(coverPreview);
      newImages.forEach((img) => revokePreview(img.preview));
    };
  }, [coverPreview, newImages]);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        setError("");

        const authToken = token || admin?.token || localStorage.getItem("adminToken");
        const config = authToken
          ? {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          : undefined;

        const { data } = await axios.get(`${API}/api/projects/${id}`, config);

        setName(typeof data.name === "string" ? data.name : "");
        setDetails(typeof data.details === "string" ? data.details : "");
        setCategory(typeof data.category === "string" ? data.category : "");
        setStarred(Boolean(data.starred));
        setCoverImage(null);
        const coverUrl = typeof data.coverImage === "string" ? data.coverImage : "";
        setExistingCover(coverUrl);
        setCoverPreview(coverUrl);

        const normalizedImages = normalizeImages(data.images);
        const galleryImages = coverUrl
          ? normalizedImages.filter((img) => img.url !== coverUrl)
          : normalizedImages;

        setExistingImages(galleryImages);
        setOriginalImages(galleryImages);
        setNewImages([]);
        setUploadProgress({ global: 0 });
      } catch (fetchError) {
        setError(fetchError.response?.data?.message || "Failed to fetch project data");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [admin?.token, id, token]);

  const validateImage = (file) => {
    if (!file) return "No file selected";

    const validExt = /\.(jpg|jpeg|png|webp)$/i.test(file.name);
    const validMime = !file.type || ACCEPTED_IMAGE_TYPES.includes(file.type);

    if (!validExt || !validMime) return "Only JPG, PNG, and WebP images are allowed";
    if (file.size > MAX_IMAGE_SIZE) return "Image must be under 2MB";

    return null;
  };

  const compressImage = async (file) => {
    try {
      return await imageCompression(file, {
        maxSizeMB: 0.8,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      });
    } catch {
      return file;
    }
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files?.[0];
    const validationError = validateImage(file);

    if (validationError) {
      setError(validationError);
      return;
    }

    const compressed = await compressImage(file);
    revokePreview(coverPreview);

    setCoverImage(compressed);
    setCoverPreview(URL.createObjectURL(compressed));
    setError("");
  };

  const removeCover = () => {
    if (!coverImage) {
      return;
    }

    revokePreview(coverPreview);
    setCoverImage(null);
    setCoverPreview(existingCover);

    if (coverInputRef.current) {
      coverInputRef.current.value = "";
    }
  };

  const handleGalleryImages = async (files) => {
    const selectedFiles = files.filter((file) => file?.type || file?.name);
    const totalImages = existingImages.length + newImages.length + selectedFiles.length;

    if (totalImages > MAX_OTHER_IMAGES) {
      setError(`Max ${MAX_OTHER_IMAGES} images allowed`);
      return;
    }

    const processedImages = [];

    for (const file of selectedFiles) {
      const validationError = validateImage(file);

      if (validationError) {
        setError(validationError);
        return;
      }

      const compressed = await compressImage(file);
      processedImages.push({
        file: compressed,
        preview: URL.createObjectURL(compressed),
      });
    }

    setNewImages((prev) => [...prev, ...processedImages]);
    setError("");
  };

  const handleNewImagesChange = (e) => {
    handleGalleryImages([...e.target.files]);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleGalleryImages([...e.dataTransfer.files]);
  };

  const handleDragOver = (e) => e.preventDefault();

  const removeNewImage = (index) => {
    setNewImages((prev) =>
      prev.filter((img, i) => {
        if (i === index) {
          revokePreview(img.preview);
          return false;
        }
        return true;
      }),
    );
  };

  const removeExistingImage = (publicId) => {
    setExistingImages((prev) => {
      const nextImages = prev.filter((img) => img.public_id !== publicId);
      const removedImage = prev.find((img) => img.public_id === publicId);

      if (!coverImage && removedImage?.url === coverPreview) {
        setCoverPreview(nextImages[0]?.url || "");
      }

      return nextImages;
    });
  };

  const validateForm = () => {
    const trimmedName = name.trim();
    const trimmedDetails = details.trim();
    const totalImages = existingImages.length + newImages.length;
    const hasCover = coverImage || coverPreview;

    if (!trimmedName) return "Project name is required";
    if (!trimmedDetails) return "Project details are required";
    if (!category || !CATEGORIES.includes(category)) return "Select a valid category";
    if (!hasCover) return "Cover image is required";
    if (totalImages > MAX_OTHER_IMAGES) return `Max ${MAX_OTHER_IMAGES} images allowed`;

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setUploadProgress({ global: 0 });

      const authToken = token || admin?.token || localStorage.getItem("adminToken");
      if (!authToken) {
        throw new Error("Not authenticated");
      }

      const removedImages = originalImages
        .filter(
          (img) =>
            img.public_id &&
            !existingImages.some((current) => current.public_id === img.public_id),
        )
        .map((img) => img.public_id)
        .filter(Boolean);
      const shouldRemoveCover =
        !coverImage &&
        Boolean(existingCover) &&
        coverPreview !== existingCover;

      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("details", details.trim());
      formData.append("category", category);
      formData.append("starred", String(starred));
      formData.append("removedImages", JSON.stringify(removedImages));
      formData.append("removeCover", String(shouldRemoveCover));

      if (coverImage) {
        formData.append("coverImage", coverImage);
      }

      newImages.forEach((img) => formData.append("images", img.file));

        await axios.put(`${API}/api/projects/${id}`, formData, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setUploadProgress({ global: percent });
        },
      });

      alert("Project updated successfully!");
      navigate("/admin/manage-projects");
    } catch (submitError) {
      setError(
        submitError.response?.data?.message ||
          submitError.message ||
          "Failed to update project",
      );
    } finally {
      setSaving(false);
    }
  };

  const remainingImages = MAX_OTHER_IMAGES - (existingImages.length + newImages.length);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 text-white">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto" />
          <p className="text-gray-300 text-sm">Loading project...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 text-white p-6">
      <button
        type="button"
        onClick={() => navigate("/admin/manage-projects")}
        className="mb-6 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition"
      >
        ← Back to Projects
      </button>

      <form onSubmit={handleSubmit} className="max-w-5xl mx-auto space-y-6">
        <div className="bg-white/10 p-5 rounded-2xl border border-white/10">
          <h1 className="text-2xl font-semibold">Edit Project</h1>
          <p className="text-gray-400 text-sm">
            Update project details and manage the project gallery
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="bg-red-500/10 border border-red-500 text-red-300 p-3 rounded-lg"
          >
            {error}
          </div>
        )}

        <div className="bg-white/10 p-6 rounded-2xl space-y-4 border border-white/10">
          <div className="grid gap-2">
            <label htmlFor="project-name" className="text-sm font-medium text-gray-200">
              Project Name
            </label>
            <input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_NAME}
              placeholder="Project Name"
              autoComplete="off"
              required
              className="w-full p-3 rounded-lg bg-white/5 border border-white/10 focus:outline-none focus:border-green-500"
            />
            <div className="text-xs text-gray-400 mt-1 text-right">
              {MAX_NAME - name.length} characters remaining
            </div>
          </div>

          <div className="grid gap-2">
            <label htmlFor="project-category" className="text-sm font-medium text-gray-200">
              Category
            </label>
            <select
              id="project-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
              className="w-full p-3 rounded-lg bg-gray-800 border border-white/10 focus:outline-none focus:border-green-500"
            >
              <option value="">Select Category</option>
              {CATEGORIES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-2">
            <label htmlFor="project-details" className="text-sm font-medium text-gray-200">
              Details
            </label>
            <textarea
              id="project-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={MAX_DETAILS}
              rows={5}
              placeholder="Project details..."
              required
              className="w-full p-3 rounded-lg bg-white/5 border border-white/10 focus:outline-none focus:border-green-500"
            />
            <div className="text-xs text-gray-400 mt-1 text-right">
              {MAX_DETAILS - details.length} characters remaining
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={starred}
              onChange={(e) => setStarred(e.target.checked)}
            />
            Feature on homepage
          </label>
        </div>

        <div className="bg-white/10 p-6 rounded-2xl border border-white/10 space-y-3">
          <h2 className="font-semibold">Cover Image</h2>
          <p className="text-sm text-gray-400">
            Upload a new cover image if you want to replace the current one.
          </p>

          <input
            ref={coverInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
            onChange={handleCoverChange}
          />

          {coverPreview && (
            <div className="relative w-40">
              <img
                src={coverPreview}
                alt="Project cover preview"
                className="w-40 h-40 object-cover rounded-xl border border-white/10"
              />
              {coverImage && (
                <button
                  type="button"
                  onClick={removeCover}
                  className="absolute top-2 right-2 bg-red-600 w-7 h-7 rounded-full"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>

        <div className="bg-white/10 p-6 rounded-2xl border border-white/10 space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-semibold">Project Gallery</h2>
              <p className="text-sm text-gray-400 mt-1">
                Manage existing images and upload new gallery visuals.
              </p>
            </div>
            <span className="text-xs text-gray-400 shrink-0">
              {remainingImages} slots remaining
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {existingImages.map((img, index) => (
              <div key={img.public_id || `${img.url}-${index}`} className="relative">
                <img
                  src={img.url}
                  alt={`Existing project ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeExistingImage(img.public_id)}
                  disabled={!img.public_id}
                  className="absolute top-1 right-1 bg-red-600 w-6 h-6 rounded-full"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-white/20 p-8 rounded-xl text-center text-gray-300 hover:border-green-500 transition"
          >
            Drag & drop gallery images here or upload below
            <input
              ref={galleryInputRef}
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              onChange={handleNewImagesChange}
              className="mt-3 block mx-auto"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {newImages.map((img, index) => (
              <div key={index} className="relative">
                <img
                  src={img.preview}
                  alt={`New project ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeNewImage(index)}
                  className="absolute top-1 right-1 bg-black/70 w-6 h-6 rounded-full"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {uploadProgress.global > 0 && (
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all"
                style={{ width: `${uploadProgress.global}%` }}
              />
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed py-3 rounded-xl font-semibold"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </form>
    </div>
  );
};

export default EditProject;
